/**
 * 픽셀 화면용 아주 작은 그리기 도구 (160×100, 팔레트 기반)
 * 이미지 파일 없이 사각형·도트·문자열 스프라이트로만 그립니다.
 */
export const W = 160;
export const H = 100;
export const FPS = 12; // 픽셀 애니메이션 느낌을 위해 일부러 끊어서

export const P = {
  0: "#0b0a12", // 거의 검정
  1: "#1b1830", // 밤 벽
  2: "#2c2547",
  3: "#4a3b6b",
  4: "#f5931f", // 망고 주황
  5: "#ffcf6b", // 따뜻한 노랑
  6: "#fff4dc", // 크림
  7: "#7cc6ff", // 화면 빛
  8: "#3a6ea5",
  9: "#ff6b8a", // 분홍
  a: "#8a7f99",
  b: "#5b5368",
  c: "#e0b48f", // 피부
  d: "#6b4a33", // 나무
  e: "#3e2a1e", // 어두운 나무
  f: "#9be27a", // 초록
  g: "#b9b4c4", // 아침 벽
  h: "#d9e6f2", // 아침 하늘
  i: "#8f8aa0",
  j: "#8c2f4a", // 커튼
  k: "#5a3e2b", // 카페 벽
  l: "#2a1d16",
  m: "#c46a12", // 진한 주황
};

const BAYER = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
];
export const bayer = (x, y) => (BAYER[y & 3][x & 3] + 0.5) / 16;

/** 결정적인 난수 (장면마다 같은 별·창문 배치) */
export const rng = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const DIGITS = {
  0: ["###", "#.#", "#.#", "#.#", "###"],
  1: [".#.", "##.", ".#.", ".#.", "###"],
  2: ["###", "..#", "###", "#..", "###"],
  3: ["###", "..#", "###", "..#", "###"],
  4: ["#.#", "#.#", "###", "..#", "..#"],
  5: ["###", "#..", "###", "..#", "###"],
  6: ["###", "#..", "###", "#.#", "###"],
  7: ["###", "..#", "..#", "..#", "..#"],
  8: ["###", "#.#", "###", "#.#", "###"],
  9: ["###", "#.#", "###", "..#", "###"],
  ",": ["...", "...", "...", ".#.", "#.."],
  "?": ["###", "..#", ".##", "...", ".#."],
  "!": [".#.", ".#.", ".#.", "...", ".#."],
  "+": ["...", ".#.", "###", ".#.", "..."],
};

export const GLYPH = {
  at: [".###.", "#...#", "#.###", "#.##.", ".#..."],
  check: ["....#", "...##", "#.##.", "###..", ".#..."],
  heart: [".#.#.", "#####", "#####", ".###.", "..#.."],
  crown: ["#..#..#", "##.#.##", "#######", "#######"],
  drop: ["..#..", ".###.", ".###.", "#####", "#####", ".###."],
  eye: [".###.", "#.#.#", ".###."],
  star: ["..#..", ".###.", "#####", ".###.", "#.#.#"],
  sparkle: [".#.", "###", ".#."],
  arrow: ["#....", "##...", "###..", "####.", "#####", "##...", "#.#.."],
  book: ["####", "#66#", "#66#", "####"],
  globe: [".###.", "#.#.#", "#####", "#.#.#", ".###."],
  card: ["#####", "#...#", "#####", "#...#", "#####"],
  mic: [".#.", "###", "###", ".#.", "###"],
  camera: [".##..", "#####", "#.#.#", "#####"],
};

export const makePainter = (ctx) => {
  const col = (c) => P[c] ?? c;
  const g = {
    ctx,
    rect(x, y, w, h, c) {
      if (w <= 0 || h <= 0) return;
      ctx.fillStyle = col(c);
      ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
    },
    px(x, y, c) {
      ctx.fillStyle = col(c);
      ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
    },
    /** 문자열 스프라이트. '.' 과 ' ' 은 투명, '#' 은 tint, 나머지는 팔레트 키 */
    sprite(rows, x, y, { tint = "6", flip = false, map } = {}) {
      x = Math.round(x);
      y = Math.round(y);
      rows.forEach((row, j) => {
        for (let i = 0; i < row.length; i++) {
          const ch = row[flip ? row.length - 1 - i : i];
          if (ch === "." || ch === " ") continue;
          const c = ch === "#" ? tint : map?.[ch] ?? ch;
          ctx.fillStyle = col(c);
          ctx.fillRect(x + i, y + j, 1, 1);
        }
      });
    },
    /** 4×4 베이어 디더 (level 0~1) */
    dither(x, y, w, h, c, level) {
      if (level <= 0) return;
      ctx.fillStyle = col(c);
      for (let j = Math.max(0, y | 0); j < Math.min(H, y + h); j++)
        for (let i = Math.max(0, x | 0); i < Math.min(W, x + w); i++)
          if (bayer(i, j) < level) ctx.fillRect(i, j, 1, 1);
    },
    /** 화면 전체 색조 (반투명) */
    tint(c, alpha) {
      ctx.globalAlpha = alpha;
      ctx.fillStyle = col(c);
      ctx.fillRect(0, 0, W, H);
      ctx.globalAlpha = 1;
    },
    /** 둥근 빛 번짐 (겹친 반투명 원 3단계, 도트 무늬 없이) */
    glow(cx, cy, r, c, level) {
      [1, 0.68, 0.4].forEach((k) => {
        ctx.globalAlpha = Math.min(0.35, level * 0.22);
        g.circle(cx, cy, Math.max(1, Math.round(r * k)), c);
      });
      ctx.globalAlpha = 1;
    },
    circle(cx, cy, r, c) {
      ctx.fillStyle = col(c);
      for (let j = -r; j <= r; j++)
        for (let i = -r; i <= r; i++)
          if (i * i + j * j <= r * r + r * 0.6) ctx.fillRect(Math.round(cx + i), Math.round(cy + j), 1, 1);
    },
    text(str, x, y, c) {
      let cx = Math.round(x);
      String(str)
        .split("")
        .forEach((ch) => {
          const d = DIGITS[ch];
          if (d) g.sprite(d, cx, y, { tint: c });
          cx += 4;
        });
    },
    /** 창·말풍선 테두리 상자 */
    box(x, y, w, h, fill, line) {
      g.rect(x + 1, y, w - 2, h, line);
      g.rect(x, y + 1, w, h - 2, line);
      g.rect(x + 1, y + 1, w - 2, h - 2, fill);
    },
  };
  return g;
};

/* ---------- 공용 스프라이트 ---------- */

// 앉아 있는 '나' (뒷모습, 주황 후드)
export const ME_SIT = [
  "....0000....",
  "...000000...",
  "..00000000..",
  "..00000000..",
  "..00000000..",
  "...000000...",
  "....cccc....",
  "..4mmmmmm4..",
  ".444444444m.",
  "44444444444m",
  "44444444444m",
  "4444444444mm",
  "4444444444mm",
  "444444444mmm",
];

// 서 있는 사람 (뒷모습). 색은 map 으로 바꿔 씀
export const PERSON = [
  "..0000..",
  ".000000.",
  ".000000.",
  ".000000.",
  "..0000..",
  "...cc...",
  ".444444.",
  "44444444",
  "44444444",
  "44444444",
  "44444444",
  "44444444",
  ".444444.",
  ".444444.",
  ".11..11.",
  ".11..11.",
  ".11..11.",
  ".00..00.",
];

export const silhouette = (c) => ({ 0: c, 4: c, c: c, 1: c, m: c });

/* ---------- 2등신 캐릭터 (귀여운 버전) ---------- */

// 옆모습으로 앉은 나 (오른쪽을 봄). 눈 (9,5), 볼 (10,6)
export const CHIBI_SIDE = [
  "....00000.....",
  "..000000000...",
  ".00000000000..",
  ".000000000cc..",
  ".0000000cccc..",
  ".000000cc0cc..",
  ".00000ccccc9..",
  "..0000cccccc..",
  "...00ccccc....",
  ".....4444.....",
  "....444444....",
  "...44444444cc.",
  "...4444444....",
  "...mmmmmmm....",
];

// 앞모습 서 있는 사람. 눈 (3,4) (6,4), 볼 (2,5) (7,5)
export const CHIBI = [
  "..000000..",
  ".00000000.",
  "0000000000",
  "00cccccc00",
  "0cc0cc0cc0",
  "0c9cccc9c0",
  ".cccccccc.",
  "..cccccc..",
  "...4444...",
  "..444444..",
  ".c444444c.",
  "..444444..",
  "..44..44..",
  "..11..11..",
];

